import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ShapeSkeleton } from "@/client/components/page-pending";
import { PageFailed } from "@/client/components/route-states";
import { useTeamShape } from "@/client/components/team-frame";
import { TeamInvite, TeamToday } from "@/client/components/team-today";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { ensureTenantData } from "@/client/lib/session";
import { tasksQuery } from "@/client/lib/tasks-query";
import { useNow } from "@/client/lib/use-now";
import { todayQuery } from "@/features/attendance/attendance-query";
import { dayIn } from "@/features/attendance/rules";
import { leaveOffQuery, waitingLeaveQuery } from "@/features/leave/leave-query";
import { PeopleList } from "@/features/people/components/people-list";
import { peopleQuery } from "@/features/people/people-query";
import { runsTeam } from "@/features/people/roles";
import { projectsQuery } from "@/features/tasks/projects-query";

/** Who is in and what is late moves through the day, so the screen re-reads the clock each minute. */
const TICK = 60_000;

export const Route = createFileRoute("/_authenticated/$slug/team/")({
  head: async ({ match }) => {
    const { peopleCopy } = await import("@/features/people/copy");
    return {
      meta: [{ title: pageTitle(peopleCopy.title, match.context.companyName) }],
    };
  },
  // Each read is skipped where its module is off or the role would be refused, so nothing a
  // module leaves out is ever asked for.
  loader: async ({ context }) => {
    const reads: Promise<unknown>[] = [
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, projectsQuery),
      ensureTenantData(context.queryClient, tasksQuery),
    ];
    if (runsTeam(context.role)) {
      const day = dayIn(context.companyTimezone, new Date());
      if (context.companyModules.includes("leave")) {
        reads.push(ensureTenantData(context.queryClient, waitingLeaveQuery));
      }
      if (context.companyModules.includes("attendance")) {
        reads.push(
          ensureTenantData(context.queryClient, todayQuery(day)),
          ensureTenantData(context.queryClient, scheduleQuery),
          ensureTenantData(
            context.queryClient,
            holidaysQuery(Number(day.slice(0, 4))),
          ),
        );
        if (context.companyModules.includes("leave")) {
          reads.push(ensureTenantData(context.queryClient, leaveOffQuery));
        }
      }
    }
    await Promise.all(reads);
  },
  pendingMs: PENDING_MS,
  pendingComponent: TodayPending,
  errorComponent: PageFailed,
  component: Team,
});

/** Today's first load once the frame is drawn: Invite in the toolbar, the shape's skeleton below. */
function TodayPending() {
  const { role } = Route.useRouteContext();
  const shape = useTeamShape();
  return (
    <>
      {runsTeam(role) ? <TeamInvite /> : null}
      <ShapeSkeleton shape={shape} lines={2} />
    </>
  );
}

function Team() {
  const { role } = Route.useRouteContext();
  const now = useNow(TICK);
  return runsTeam(role) ? <TeamToday now={now} /> : <Colleagues now={now} />;
}

/** An Employee's Team is the list of who is here and which projects they share. */
function Colleagues({ now }: { readonly now: Date }) {
  const { slug } = Route.useParams();
  const { role, email, companyTimezone } = Route.useRouteContext();
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: projects } = useSuspenseQuery(projectsQuery);
  const { data: tasks } = useSuspenseQuery(tasksQuery);
  const names = new Map(projects.map((project) => [project.id, project.name]));
  // Nobody is added to a company-wide project, so an open task in one puts its holder in it.
  const everyone = new Set(
    projects
      .filter((project) => project.access === "company" && !project.archivedAt)
      .map((project) => project.id),
  );
  const projectsOf = (personId: string) => {
    const ids = new Set(
      people.find((row) => row.id === personId)?.projectIds ?? [],
    );
    for (const task of tasks) {
      if (
        task.assigneeId === personId &&
        task.status !== "done" &&
        task.projectId !== null &&
        everyone.has(task.projectId)
      ) {
        ids.add(task.projectId);
      }
    }
    return [...ids].flatMap((id) => names.get(id) ?? []);
  };
  return (
    <PeopleList
      slug={slug}
      people={people.filter((row) => row.state === "active")}
      q=""
      onClear={() => undefined}
      viewerRole={role}
      viewerEmail={email ?? ""}
      timeZone={companyTimezone}
      now={now}
      projects={projectsOf}
    />
  );
}
